import { defineStore } from 'pinia'
import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import type {
  Message,
  MessageEventData,
  MessageStatusEventData,
  MessageContent,
  UserSendTemplateMessageEvent,
} from '../types/interfaces'
import { MessageType, MessageSourceType, MessageStatus, ChatStatus } from '../types/enums'
import {
  getMessagePreviewText,
  normalizeMessageStatusValue,
} from '../services/messageContentFormatter'
import { sortChatMessages } from '../services/messageOrdering'

import { useChatStore } from './useChatStore'

const FINISHED_CHAT_STATUSES = [ChatStatus.FINISH, ChatStatus.AUTO_CLOSED]

export const useMessageStore = defineStore('message', () => {
  const chatStore = useChatStore()

  const sendingMessages = ref<Set<string>>(new Set())

  const isMediaSource = (value: unknown): value is string => {
    if (typeof value !== 'string') return false

    return /^(https?:|blob:|data:|\/)/i.test(value)
  }

  const normalizeMediaCaption = (caption: unknown, messageType: MessageType) => {
    if (typeof caption !== 'string') return null

    const trimmedCaption = caption.trim()
    const isVisualMedia = messageType === MessageType.IMAGE || messageType === MessageType.VIDEO
    if (!trimmedCaption || (isVisualMedia && trimmedCaption.toLowerCase() === messageType)) {
      return null
    }

    return caption
  }

  const normalizeParsedMessageContent = (parsedContent: Record<string, any>): MessageContent => {
    const messageType = (String(parsedContent.type || 'text').toLowerCase() ||
      'text') as MessageType
    const isAudio = messageType === MessageType.AUDIO
    const caption = normalizeMediaCaption(parsedContent.caption, messageType)

    const context = parsedContent.context
      ? {
          from: parsedContent.context.from || null,
          from_user_id: parsedContent.context.from_user_id || null,
          id: parsedContent.context.id || null,
          gs_id: parsedContent.context.gs_id || null,
          meta_msg_id: parsedContent.context.meta_msg_id || null,
        }
      : {
          from: null,
          from_user_id: null,
          id: null,
          gs_id: null,
          meta_msg_id: null,
        }

    return {
      type: messageType,
      content: isAudio ? caption || '' : parsedContent.content || '',
      text: isAudio ? caption || '' : parsedContent.text || parsedContent.content || '',
      content_type: parsedContent.content_type || parsedContent.file_content_type || null,
      url: isAudio
        ? parsedContent.url || (isMediaSource(parsedContent.content) ? parsedContent.content : null)
        : parsedContent.url || null,
      file_name: parsedContent.file_name || null,
      file_size: parsedContent.file_size || null,
      duration: parsedContent.duration || null,
      longitude: parsedContent.longitude || null,
      latitude: parsedContent.latitude || null,
      name: parsedContent.name || null,
      address: parsedContent.address || null,
      contacts: Array.isArray(parsedContent.contacts) ? parsedContent.contacts : null,
      context: context,
      caption,
      is_response_template: parsedContent.is_response_template ?? null,
      template_id: parsedContent.template_id || null,
      ai_translate:
        parsedContent.ai_translate && typeof parsedContent.ai_translate === 'object'
          ? parsedContent.ai_translate
          : null,
    }
  }

  const parseMessageContent = (content: unknown): MessageContent => {
    if (typeof content === 'string') {
      try {
        const parsedContent = JSON.parse(content)
        return normalizeParsedMessageContent(parsedContent)
      } catch {
        return {
          type: MessageType.TEXT,
          content: String(content),
          text: String(content),
          context: {
            from: null,
            from_user_id: null,
            id: null,
          },
        }
      }
    }

    if (content && typeof content === 'object') {
      return normalizeParsedMessageContent(content as Record<string, any>)
    }

    return content as MessageContent
  }

  /**
   * Aplica un MESSAGE_EVENT al chat. Devuelve `true` si el evento trajo un mensaje
   * que no estaba, y `false` si se descartó o solo repitió uno ya conocido: quien
   * llama lo usa para no notificar ni contar dos veces el mismo mensaje.
   */
  const handleMessageEvent = (data: MessageEventData): boolean => {
    const chatIndex = chatStore.chats.findIndex((chat) => chat.chat_id === data.chat_id)

    if (chatIndex !== -1) {
      const chat = chatStore.chats[chatIndex]

      if (FINISHED_CHAT_STATUSES.includes(chat.status)) {
        return false
      }

      const tempMessageIndex = chat.messages.findIndex((msg) => {
        if (
          msg.status !== MessageStatus.PENDING ||
          msg.source_type !== MessageSourceType.SALIENTE
        ) {
          return false
        }

        if (msg.provider_message_id && data.content.provider_message_id) {
          if (msg.provider_message_id === data.content.provider_message_id) {
            return true
          }
        }

        if (
          msg.content.type === MessageType.TEXT &&
          msg.content.content === data.content.content?.content
        ) {
          return true
        }
        if (
          msg.content.file_name &&
          data.content.content?.file_name &&
          msg.content.file_name === data.content.content.file_name
        ) {
          return true
        }

        return false
      })

      const parsedMessage: Message = {
        ...data.content,
        content: parseMessageContent(data.content.content),
      }

      let isRepeated = false

      if (tempMessageIndex !== -1) {
        parsedMessage.content.ai_translate =
          parsedMessage.content.ai_translate ?? chat.messages[tempMessageIndex].content.ai_translate
        chat.messages[tempMessageIndex] = parsedMessage
        sendingMessages.value.delete(chat.messages[tempMessageIndex].id)
      } else {
        // Verificar si el mensaje ya existe
        const existingIndex = chat.messages.findIndex((m) => m.id === parsedMessage.id)
        if (existingIndex !== -1) {
          chat.messages[existingIndex] = parsedMessage
          isRepeated = true
        } else {
          chat.messages.push(parsedMessage)
        }
      }

      sortChatMessages(chat.messages)

      chat.last_message = getMessagePreviewText(parsedMessage.content)
      chat.last_message_time = data.content.date

      if (
        !isRepeated &&
        data.content.source_type === MessageSourceType.ENTRANTE &&
        chatStore.selectedChatId !== data.chat_id
      ) {
        chatStore.incrementUnreadCount(data.chat_id)
      }

      return !isRepeated
    } else {
      const parsedMessage: Message = {
        ...data.content,
        content: parseMessageContent(data.content.content),
      }

      const newChat = {
        id: data.chat_id,
        chat_id: data.chat_id,
        contact_name: 'Nuevo Contacto',
        contact_phone: '',
        last_message: getMessagePreviewText(parsedMessage.content),
        last_message_time: data.content.date,
        unread_count:
          data.content.source_type === MessageSourceType.ENTRANTE &&
          chatStore.selectedChatId !== data.chat_id
            ? 1
            : 0,
        status: ChatStatus.PENDING,
        campaign_id: '',
        incoming_route_id: data.chat?.incoming_route_id || '',
        messages: [parsedMessage],
        // Sin fecha de llegada el chat queda fuera del orden de la lista. El
        // evento trae la del chat; si no, la del mensaje que lo dio a conocer.
        created_at: data.chat?.start_date || data.content.date,
      }

      chatStore.addChat(newChat)

      return true
    }
  }

  const handleUserSendTemplateMessage = (data: UserSendTemplateMessageEvent) => {
    const chat = chatStore.chats.find((item) => item.chat_id === data.chat_id)
    if (!chat) {
      return
    }

    if (FINISHED_CHAT_STATUSES.includes(chat.status)) {
      return
    }

    const parsedMessage: Message = {
      ...data.template_message,
      chat_id: data.chat_id,
      content: parseMessageContent(data.template_message.content),
    }

    const existingIndex = chat.messages.findIndex((message) => {
      if (parsedMessage.id && message.id === parsedMessage.id) {
        return true
      }

      if (
        parsedMessage.provider_message_id &&
        message.provider_message_id === parsedMessage.provider_message_id
      ) {
        return true
      }

      return false
    })
    if (existingIndex !== -1) {
      chat.messages[existingIndex] = parsedMessage
    } else {
      chat.messages.push(parsedMessage)
    }

    sortChatMessages(chat.messages)
    chat.last_message = getMessagePreviewText(parsedMessage.content)
    chat.last_message_time = parsedMessage.date
    chatStore.markChatAsRead(data.chat_id)

    if (chat.status === ChatStatus.EXPIRE) {
      chat.status = ChatStatus.TEMPLATE
    }
  }

  const handleMessageStatusEvent = (data: MessageStatusEventData) => {
    const chat = chatStore.chats.find((c) => c.chat_id === data.chat_id)
    if (!chat) {
      return
    }

    if (FINISHED_CHAT_STATUSES.includes(chat.status)) {
      return
    }

    let message = chat.messages.find((m) => m.id === data.message_id)

    if (!message && data.provider_message_id) {
      message = chat.messages.find((m) => m.provider_message_id === data.provider_message_id)
    }

    if (!message) {
      // El array ya viene en orden cronológico; el consecutivo no sirve para elegir
      // aquí porque se reinicia en cada sesión del historial.
      const pendingMessages = chat.messages.filter(
        (m) =>
          [MessageSourceType.SALIENTE, MessageSourceType.TEMPLATE].includes(m.source_type) &&
          [MessageStatus.PENDING, MessageStatus.ENQUEUED].includes(
            normalizeMessageStatusValue(m.status) || MessageStatus.PENDING,
          ),
      )
      message = pendingMessages[pendingMessages.length - 1]
    }

    if (message) {
      const statuses = Array.isArray(data.status) ? data.status : [data.status]
      const normalizedStatus = [...statuses]
        .reverse()
        .map((status) => normalizeMessageStatusValue(status))
        .find((status): status is MessageStatus => status !== null)

      if (!normalizedStatus) {
        return
      }

      message.status = normalizedStatus

      // El motivo viaja solo en el evento FAILED; los demás estados no lo borran.
      if (normalizedStatus === MessageStatus.FAILED) {
        message.failure_code = data.failure_code ?? message.failure_code
        message.failure_reason = data.failure_reason ?? message.failure_reason
      }

      if (data.provider_message_id && !message.provider_message_id) {
        message.provider_message_id = data.provider_message_id
      }
      if (
        message.status &&
        [MessageStatus.SENT, MessageStatus.DELIVERED, MessageStatus.READ].includes(message.status)
      ) {
        sendingMessages.value.delete(message.id)
      }
    }
  }

  const createTemporaryMessage = (
    chatId: string,
    content: MessageContent,
    sourceType: MessageSourceType = MessageSourceType.SALIENTE,
    userId?: string,
  ): Message => {
    const tempId = uuidv4()
    const chat = chatStore.chats.find((c) => c.chat_id === chatId)
    const nextConsecutive = chat ? Math.max(...chat.messages.map((m) => m.consecutive), 0) + 1 : 1

    const message: Message = {
      id: tempId,
      chat_id: chatId,
      source_type: sourceType,
      date: new Date().toISOString(),
      provider_message_id: '',
      consecutive: nextConsecutive,
      status: MessageStatus.PENDING,
      content,
      user_id: userId || chatStore.currentUserId,
    }

    return message
  }

  const addTemporaryMessage = (message: Message) => {
    const chat = chatStore.chats.find((c) => c.chat_id === message.chat_id)
    if (chat) {
      chat.messages.push(message)
      sendingMessages.value.add(message.id)

      chat.last_message = getMessagePreviewText(message.content)
      chat.last_message_time = message.date
      chatStore.markChatAsRead(message.chat_id)
    }
  }

  const createTextMessage = (
    chatId: string,
    text: string,
    userId?: string,
    replyToId?: string,
    aiTranslate?: MessageContent['ai_translate'],
  ) => {
    const content: MessageContent = {
      type: MessageType.TEXT,
      text: text,
      context: {
        from: null,
        from_user_id: null,
        id: replyToId || null,
        gs_id: null,
        meta_msg_id: null,
      },
    }

    if (aiTranslate && Object.keys(aiTranslate).length > 0) {
      content.ai_translate = aiTranslate
    }

    return createTemporaryMessage(chatId, content, MessageSourceType.SALIENTE, userId)
  }

  const createFileMessage = (chatId: string, file: File, userId?: string, caption?: string) => {
    const messageType = getMessageTypeFromFile(file)
    const fileUrl = URL.createObjectURL(file)
    const fileLabel = `${file.name} (${(file.size / 1024).toFixed(1)}KB)`
    const messageContent = messageType === MessageType.IMAGE ? caption || '' : caption || fileLabel

    return createTemporaryMessage(
      chatId,
      {
        type: messageType,
        file_name: file.name,
        file_size: file.size.toString(),
        url: fileUrl,
        content: messageContent,
        caption: caption || null,
        context: {
          from: null,
          from_user_id: null,
          id: null,
        },
      },
      MessageSourceType.SALIENTE,
      userId,
    )
  }

  const getMessageTypeFromFile = (file: File): MessageType => {
    const type = file.type.toLowerCase()

    if (type.startsWith('image/')) {
      return MessageType.IMAGE
    } else if (type.startsWith('video/')) {
      return MessageType.VIDEO
    } else if (type.startsWith('audio/')) {
      return MessageType.AUDIO
    } else {
      return MessageType.DOCUMENT
    }
  }

  const getChatMessages = (chatId: string) => {
    const chat = chatStore.chats.find((c) => c.chat_id === chatId)
    return chat?.messages || []
  }

  const isMessageSending = (messageId: string) => {
    return sendingMessages.value.has(messageId)
  }

  const markMessageAsFailed = (messageId: string) => {
    for (const chat of chatStore.chats) {
      const message = chat.messages.find((m) => m.id === messageId)
      if (message) {
        message.status = MessageStatus.FAILED
        sendingMessages.value.delete(messageId)
        break
      }
    }
  }

  const clearSendingMessages = () => {
    sendingMessages.value.clear()
  }

  const applyMessageTranslation = (
    messageId: string,
    aiTranslate: NonNullable<MessageContent['ai_translate']>,
  ) => {
    for (const chat of chatStore.chats) {
      const message = chat.messages.find((m) => m.id === messageId)
      if (!message) continue

      message.content.ai_translate = {
        ...message.content.ai_translate,
        ...aiTranslate,
      }
      break
    }
  }

  const applyMessageTranslations = (
    translations: Array<{
      message_id: string
      ai_translate: NonNullable<MessageContent['ai_translate']>
    }>,
  ) => {
    translations.forEach((translation) => {
      applyMessageTranslation(translation.message_id, translation.ai_translate)
    })
  }

  return {
    sendingMessages,
    handleMessageEvent,
    handleUserSendTemplateMessage,
    handleMessageStatusEvent,
    createTemporaryMessage,
    addTemporaryMessage,
    createTextMessage,
    createFileMessage,
    getChatMessages,
    isMessageSending,
    markMessageAsFailed,
    clearSendingMessages,
    applyMessageTranslation,
    applyMessageTranslations,
    getMessageTypeFromFile,
  }
})
